import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Phone, MapPin, Mail, ScrollText, Sparkles, Compass, Quote } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const TRUSTEES = [
  { name: "Shri Swarup Manoj Joshi", quote: "The essence of this journey lies in preserving and carrying forward the sacred Guru Parampara with sincerity and discipline. Every effort undertaken through the sanstha is directed towards ensuring that this knowledge and practice reach further, touching more lives with clarity and purpose. It remains my vision that this parampara continues to grow steadily and authentically — serving as a guiding light for generations to come." },
  { name: "Shri Hitesh Patel", quote: "Our traditions, much like nature, require nurturing, patience, and continuity. Through our collective efforts, we strive to create spaces where individuals can reconnect with their roots and find balance in today's fast-paced world. It is our sincere belief that when culture is preserved with care, it naturally shapes a more grounded and harmonious society." },
  { name: "Shri Kailash Agrawal", quote: "Over the years, I have witnessed how values and traditions form the foundation of a strong society. Our efforts are a humble step towards preserving these timeless principles for future generations. It gives me immense satisfaction to see people coming together with devotion and sincerity, keeping our cultural spirit alive." },
  { name: "Shri Anish Maheshwari", quote: "Growth and development are most meaningful when they are rooted in purpose. Through these initiatives, we aim to create an environment where individuals can engage, reflect, and grow together. As our journey continues, we remain focused on building something that serves not just the present, but generations to come." },
  { name: "Shri Mohan Mav", quote: "It is encouraging to witness the consistent efforts of the sanstha in preserving and promoting our cultural and spiritual traditions. The discipline and sincerity reflected in every initiative truly stand out. I believe that such dedicated work will continue to grow in strength and positively impact many more individuals in the years ahead." },
  { name: "Shrimati Sarita Gajjrela", quote: "A strong foundation is essential for any lasting structure, and the same holds true for our cultural values. Our efforts are directed towards creating meaningful experiences that bring people together and strengthen these foundations. It is a privilege to contribute towards building something that nurtures both community and tradition." },
  { name: "Shrimati Nisha Joshi", quote: "In today's modern world, staying connected to our cultural roots is more important than ever. Being part of this journey has shown me how tradition and youth can beautifully coexist. It is inspiring to see people of all ages come together, creating a vibrant and meaningful cultural experience." },
];

const LINEAGE = [
  "Shri Dakshinamurthy Guru — The Adi Guru, representing the eternal source of wisdom",
  "Bhagwat Pad Adi Shankaracharya — The great reviver of Advaita Vedanta",
  "Shivanand Saraswati",
  "Adwayanand Natha Saraswati",
  "Maha Meganand Saraswati Sri Vidya Swami",
  "Shree Swarup Guruji (Dr. Manoj Diwakar Joshi) — Present Guru and guiding force",
];

const STAGES = [
  { icon: Compass, title: "Attaining Control over the Body", body: "Developing discipline over the physical self. Through consistent practice and awareness, individuals overcome inertia and lethargy (tamas), cultivating stability, strength and conscious control over their actions." },
  { icon: Sparkles, title: "Attaining Harmony with Nature Tattva", body: "Aligning oneself with the natural elements (prakriti). By developing sensitivity and balance in interaction with the surrounding environment, one gradually transcends restlessness and excessive activity (rajas), moving towards a state of harmony and equilibrium." },
  { icon: ScrollText, title: "Attaining Alignment with Khagoliya Tattva", body: "Elevating one's consciousness towards a deeper connection with the cosmic order (khagoliya tattva). In this state, the individual attains clarity, purity and heightened awareness (sattva) — leading to a life guided by wisdom, purpose and inner stability." },
];

export default function About() {
  const [leaders, setLeaders] = useState(null);
  useEffect(() => { api.get("/leaders").then(({ data }) => setLeaders(data)).catch(() => setLeaders([])); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-16">
      {/* Hero */}
      <section className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[#B45309] mb-3">
            <Sparkles className="h-4 w-4" /><span className="text-xs uppercase tracking-[0.2em]">Trust Regn. No. MAH/670/2018/Thane</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#5C061C] leading-tight">Shri Vidya Sadhana Seva Sanstha</h1>
          <p className="mt-3 text-lg text-[#B45309] font-serif">ॐ श्री विद्या साधना सेवेभ्यो नमः</p>
          <p className="mt-5 text-[#1F1A17] leading-relaxed">
            Shri Vidya Sadhana Seva Sanstha is a spiritual and cultural organization rooted in the timeless
            traditions of Sanatana Dharma, dedicated to the holistic upliftment of individuals through
            <span className="font-semibold text-[#5C061C]"> sadhana, seva</span> and
            <span className="font-semibold text-[#5C061C]"> sanskaar</span>. The sanstha works towards
            nurturing a balanced way of life where spiritual awareness, cultural values and social
            responsibility coexist harmoniously.
          </p>
          <p className="mt-4 text-[#1F1A17] leading-relaxed">
            The foundation of the sanstha is not merely institutional, but deeply anchored in an unbroken
            <span className="font-semibold text-[#5C061C]"> Guru Parampara</span> — a sacred lineage through which
            knowledge, discipline and wisdom are transmitted from Guru to disciple across generations.
          </p>
        </div>
        <div className="rounded-3xl overflow-hidden card-warm">
          <img src="https://customer-assets-agu9un31.emergentagent.net/job_spiritual-library-7/artifacts/ruwrsaeh_pic2.jpg" alt="Sri Vidya" className="w-full h-[520px] object-cover" />
        </div>
      </section>

      {/* Spiritual Lineage */}
      <section className="card-warm rounded-3xl p-8">
        <div className="divider-om max-w-xs mb-6"><span className="font-serif text-lg">ॐ</span></div>
        <h2 className="font-serif text-3xl text-[#5C061C]">Spiritual Lineage &amp; Parampara</h2>
        <p className="mt-4 text-[#1F1A17] leading-relaxed">
          The sanstha follows the revered traditions of the <span className="font-semibold">Adi Shankaracharya Parampara</span> and the
          <span className="font-semibold"> Agasthya Rishi – Lopamudra Sampradaya</span>, both of which hold profound significance in the spiritual heritage of Bharat.
        </p>
        <ul className="mt-4 space-y-3 text-[#1F1A17] leading-relaxed list-disc pl-6">
          <li>The <span className="font-semibold">Dakshinamurthy Guru</span>, regarded as the eternal embodiment of knowledge, represents the origin of the Guru tradition — symbolising the silent transmission of truth.</li>
          <li>This lineage was revitalised and systematised by <span className="font-semibold">Bhagwat Pad Adi Shankaracharya</span>, who re-established the philosophy of Advaita and unified spiritual thought across the nation.</li>
          <li>Alongside, the tradition of <span className="font-semibold">Rishi Agasthya and Lopamudra</span> represents a powerful stream of spiritual practice where knowledge is not only preserved but lived and experienced.</li>
        </ul>

        <h3 className="mt-8 font-serif text-2xl text-[#5C061C]">Guru Parampara (Lineage of Guidance)</h3>
        <ol className="mt-4 space-y-2 text-[#1F1A17]">
          {LINEAGE.map((l, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 h-6 w-6 rounded-full bg-[#F4EEDF] text-[#5C061C] text-xs grid place-items-center font-semibold">{i + 1}</span>
              <span>{l}</span>
            </li>
          ))}
        </ol>
        <p className="mt-6 text-[#1F1A17] leading-relaxed italic">
          Under the guidance of Dr. Manoj Joshi ji, the sanstha continues to expand its activities with a focus on disciplined
          spiritual practice, cultural revival and meaningful community engagement.
        </p>
      </section>

      {/* Inspiration */}
      <section className="grid md:grid-cols-3 gap-8 items-center">
        <div className="md:col-span-2">
          <h2 className="font-serif text-3xl text-[#5C061C]">Inspiration Behind the Sanstha</h2>
          <p className="mt-4 text-[#1F1A17] leading-relaxed">
            The inspiration for this journey is deeply rooted in the life and values of
            <span className="font-semibold text-[#5C061C]"> Shri Diwakar Narleram Joshi ji</span>, whose vision and dedication laid the foundation for this path.
          </p>
          <p className="mt-3 text-[#1F1A17] leading-relaxed">
            His life reflected a commitment to simplicity, discipline and cultural integrity. The principles he upheld continue to
            guide the sanstha's direction — serving as a silent yet powerful force behind its growth and purpose.
          </p>
        </div>
        <div className="rounded-2xl overflow-hidden card-warm md:col-span-1">
          <div className="aspect-square bg-gradient-to-br from-[#F4EEDF] via-[#FAF3E0] to-[#F9E4B7] grid place-items-center">
            <span className="font-serif text-7xl text-[#5C061C]/60">ॐ</span>
          </div>
        </div>
      </section>

      {/* Philosophical Foundation */}
      <section>
        <div className="divider-om max-w-md mx-auto mb-6"><span className="font-serif text-2xl">ॐ</span></div>
        <h2 className="font-serif text-3xl text-center text-[#5C061C]">Philosophical Foundation</h2>
        <p className="mt-3 text-center text-[#6E6259] max-w-3xl mx-auto">
          Our approach to spiritual growth is rooted in a structured progression of inner refinement, guiding
          individuals through three essential stages of transformation.
        </p>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {STAGES.map((s, i) => (
            <div key={i} className="card-warm rounded-2xl p-6">
              <div className="h-12 w-12 rounded-xl bg-[#F4EEDF] grid place-items-center text-[#5C061C] mb-4">
                <s.icon className="h-6 w-6" />
              </div>
              <p className="text-xs uppercase tracking-widest text-[#B45309]">Stage {i + 1}</p>
              <h3 className="font-serif text-xl text-[#5C061C] mt-1">{s.title}</h3>
              <p className="mt-3 text-sm text-[#1F1A17] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Continuing the Journey */}
      <section className="card-warm rounded-3xl p-8">
        <h2 className="font-serif text-3xl text-[#5C061C]">Continuing the Journey</h2>
        <p className="mt-4 text-[#1F1A17] leading-relaxed">
          What began as a focused effort inspired by tradition has gradually evolved into a growing collective movement. As
          participation increases and engagement deepens, the sanstha continues to move forward with the same guiding principle —
          <span className="font-semibold text-[#5C061C]"> to preserve, practice and pass on the cultural and spiritual heritage in its truest form.</span>
        </p>
        <h3 className="mt-8 font-serif text-2xl text-[#5C061C]">Our Journey So Far</h3>
        <div className="mt-4 space-y-4 text-[#1F1A17] leading-relaxed">
          <p>Over the years, the sanstha has evolved into a living stream of continuous spiritual and cultural engagement, where practices are not confined to isolated occasions but are experienced as an ongoing journey.</p>
          <p>Throughout the annual cycle, a diverse range of sacred observances, ritual practices and collective gatherings are conducted with deep adherence to traditional methods — ensuring that each initiative becomes an opportunity for participants to immerse themselves in meaningful spiritual experience rather than mere participation.</p>
          <p>Beyond stationary activities, guided spiritual travels and immersive retreats form an integral part of the sanstha's efforts. Equally significant is the emphasis on knowledge transmission — ensuring that practices are not only performed but also understood in their true essence.</p>
        </div>
      </section>

      {/* Trustees */}
      <section>
        <div className="divider-om max-w-md mx-auto mb-6"><span className="font-serif text-2xl">ॐ</span></div>
        <h2 className="font-serif text-3xl text-center text-[#5C061C]">Our Trustees</h2>
        <p className="mt-3 text-center text-[#6E6259]">Voices from the members who shepherd the sanstha's vision.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {TRUSTEES.map((t, i) => (
            <div key={i} className="card-warm rounded-2xl p-6" data-testid={`trustee-${i}`}>
              <Quote className="h-6 w-6 text-[#D97706]" />
              <p className="mt-3 text-sm text-[#1F1A17] leading-relaxed italic">"{t.quote}"</p>
              <div className="mt-4 pt-4 border-t border-[#E8DEC8]">
                <p className="font-serif text-lg text-[#5C061C]">{t.name}</p>
                <p className="text-xs uppercase tracking-widest text-[#B45309]">Trustee</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Leaders from CMS (optional) */}
      {leaders && leaders.length > 0 && (
        <section>
          <div className="divider-om max-w-md mx-auto mb-6"><span className="font-serif text-2xl">ॐ</span></div>
          <h2 className="font-serif text-3xl text-center text-[#5C061C]">Our Leadership</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {leaders.map((l) => (
              <div key={l.id} className="card-warm rounded-2xl overflow-hidden text-center" data-testid={`leader-${l.id}`}>
                <div className="aspect-square bg-[#F4EEDF]">
                  {l.photo_url && <img src={l.photo_url} alt={l.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl text-[#5C061C]">{l.name}</h3>
                  <p className="text-xs uppercase tracking-widest text-[#B45309] mt-1">{l.role}</p>
                  {l.description && <p className="text-sm text-[#6E6259] mt-3">{l.description}</p>}
                  {l.contact && <p className="text-sm text-[#5C061C] mt-3 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {l.contact}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Contact card */}
      <section className="card-warm rounded-3xl p-8 grid md:grid-cols-3 gap-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#F4EEDF] grid place-items-center text-[#5C061C] shrink-0"><MapPin className="h-5 w-5" /></div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#B45309]">Sanstha Office</p>
            <p className="text-[#1F1A17] leading-relaxed">Flat 502, B Wing, Prakruti Pearl CHS,<br />Besides Rodas Enclave, Hiranandani Estate,<br />Thane West, Thane, Maharashtra 400607</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#F4EEDF] grid place-items-center text-[#5C061C] shrink-0"><Phone className="h-5 w-5" /></div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#B45309]">Phone / WhatsApp</p>
            <p className="text-[#1F1A17]">+91 99208 33345 (Phone)</p>
            <p className="text-[#1F1A17]">+91 99870 35050 (WhatsApp)</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#F4EEDF] grid place-items-center text-[#5C061C] shrink-0"><Mail className="h-5 w-5" /></div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#B45309]">Email</p>
            <p className="text-[#1F1A17]">shrividhya104@gmail.com</p>
          </div>
        </div>
      </section>
    </div>
  );
}
